if (window?.performance) {
	window.performance.mark('CFP-63.app');
	window.performance.mark('navigation-fmp.NavigationFMP.start');
	window.performance.mark('side-navigation-fmp.SideNavigationFMP.start');
}

import React, { Profiler, useEffect } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import createCache from '@emotion/cache';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { CacheProvider } from '@emotion/react';
import UAParser from 'ua-parser-js';
import ReactDOM from 'react-dom';
import { v4 as uuidv4 } from 'uuid';

import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import { addUFOCustomData } from '@atlaskit/react-ufo/add-ufo-custom-data';
import setupUFOGlobalErrorHandler from '@atlaskit/react-ufo/setup-ufo-global-error-handler';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { isMobileApp_checkGateViaSSR_DO_NOT_USE } from '@confluence/mobile-app-detection-common';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { initializeUFO } from '@confluence/react-ufo';
import { initializeINSM } from '@confluence/cc-insm';
import { initializeBrowserMetrics } from '@confluence/browser-metrics/entry-points/initializeBrowserMetrics.ts';
import { releaseUnusedSSRedLegacyMacrosWithDelay } from '@confluence/content-renderer-legacy-macros/entry-points/SSRedLegacyMacro.ts';
import { getSessionDataError } from '@confluence/session-data/entry-points/getSessionDataError.ts';
import { getEmptySessionData } from '@confluence/session-data/entry-points/processSessionData.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { getApolloCache } from '@confluence/graphql/entry-points/createClient.ts';
import { getAGGClient, getApolloAGGCache } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { cleanUpLoadablePlaceholders, preloadLoadable } from '@confluence/loadable';
import {
	WaterfallMeasures,
	WATERFALL_INITIAL_MEASURES,
} from '@confluence/action-measures/entry-points/WaterfallMeasures.ts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { isNativeMobile, setUserAgent } from '@confluence/browser-helper';
import { BrowserNotSupportedLoader as BrowserNotSupportedPage } from '@confluence/browser-not-supported/entry-points/BrowserNotSupportedLoader.tsx';
import { ErrorAppLoader } from '@confluence/error-app/entry-points/ErrorAppLoader.tsx';
import { SSRAppTheme, SSRReactRootTiming, SSRAppScripts } from '@confluence/ssr-app-components';
import { preloadQueriesForSPAHydration } from '@confluence/query-preloaders';
import { initializePreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { initializeAggRegionSteeringHint } from '@confluence/query-preloader-tools/entry-points/aggRegionSteeringHint.ts';
import { getSessionDataAndInitializeStatsig } from '@confluence/statsig-client/entry-points/getSessionDataAndInitializeStatsig.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { matchHydrationError } from '@confluence/ssr-utilities/entry-points/matchHydrationError.ts';
import {
	SSR_INTENTIONAL_ABORT_DIGEST,
	SSR_QUERY_BLOCKED_DIGEST,
} from '@confluence/ssr-utilities/entry-points/SSRQueryBlockedError.ts';
import { saveFullCycleOutput } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';
import { applyPersistedStatsigOverrides } from '@confluence/session-data/entry-points/persistedStatsigOverrides.ts';
import { initializeStorageManager } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import {
	ACCESS_LINK,
	EXTERNAL_SHARE,
	MOBILE_TAKEOVER_PAGE,
} from '@confluence/named-routes/entry-points/routes.ts';
import { getLogger } from '@confluence/logger';

import { coreBrowserNotSupported } from '@devtools/browser-check';

import { initializeBootstrapStorage } from './bootstrapStorageInit.js';
import configureStore from './stores/configureStore.js';
import { subscribe } from './subscriptions.js';
import Root from './components/Root/Root.jsx';
import { preloadComponentsNoStatsigSupport } from './route-component-preloader.js';
import { handleDOMGoogleTranslateIssues } from './google-translate-issue-workaround.js';
import { initialization } from './initialization.js';
import { scheduleSyncHydration } from './selective-hydration.ts';
import { createHydrationPhaseTimer } from './hydrationPhaseTiming.ts';
import { initializeVCObserver } from './initializeVCObserver.ts';
import { onDOMContentLoaded } from './onDOMContentLoaded.ts';
import { parseHydrationErrorInfo } from './helpers/hydrationLog.ts';
import { dispatchHydrationError } from './helpers/dispatchHydrationError.js';
import { tryLoadTranslation } from './tryLoadTranslation.ts';
import { devTrimConsoleTraces } from './devTrimConsoleTraces.ts';
import { fireSSRExperimentExposures } from './fireSSRExperimentExposures.js';

const logger = getLogger('SPAEntry');

// Non-streaming pages use renderToString, which leaves every still-pending Suspense boundary for the
// client without a digest. React reports that as error #419, or in development as renderToString's
// own message.
const isNonStreamingError419 = (error) =>
	!window.__SSR_REACT_STREAMING__ &&
	/Minified React error #419|The server used "renderToString" which does not support Suspense/.test(
		error?.message ?? '',
	);

// Try load translation early if we know locale from SSR
if (window.__SSR_LOCALE__) {
	void tryLoadTranslation();
}

onDOMContentLoaded(async function () {
	/**
	 * setupUFOGlobalErrorHandler MUST be the first thing executed before any rendering operations.
	 * This ensures we catch and track any errors that occur during the app's initialization,
	 * including async operations and React rendering. Moving this call later would risk missing
	 * early errors.
	 * it provides ufo:errors:globalCount is the count of all errors which occurred on the client all up
	 */
	setupUFOGlobalErrorHandler();

	// When page is being refreshed or redirected from server we run into a condition when redirect
	// url requested from the server and SPA (which resources are loaded via early HTML flush) is
	// being initialized at the same time. We don't want to waste browser resources/make network
	// calls when page is going to be redirected anyway, so bail out early.
	if (document.querySelector(`meta[http-equiv="Refresh"]`)) {
		return;
	}

	const rootNode = document.getElementById('confluence-ui');

	if (window.__HYDRATABLE__) {
		// remove nonce attributes from style tags to support react hydration
		document.querySelectorAll('style[nonce]').forEach((styleTag) => {
			styleTag.removeAttribute('nonce');
		});

		saveFullCycleOutput();
	}

	const { client, aggClient } = WaterfallMeasures.run(
		WATERFALL_INITIAL_MEASURES.CREATE_APOLLO_CLIENTS,
		() => {
			const apolloClient = getApolloClient();
			const apolloAggClient = getAGGClient();
			// Initialize the Relay environment alongside the Apollo clients so
			// `window.GLOBAL_RELAY_ENV` is set before any Relay-enabled interop
			// call (including the preloader interop path in
			// `@confluence/query-preloader-tools`). Mirrors how
			// `GLOBAL_APOLLO_AGG_CLIENT` is set during `getAGGClient()`.
			getRelayEnvironment();
			return { client: apolloClient, aggClient: apolloAggClient };
		},
	);

	let initializeEmbeddedConfluencePromise;
	// @TODO: Update this to use `isMobileApp_DO_NOT_USE()` once we remove the experiment-gate
	if (isMobileApp_checkGateViaSSR_DO_NOT_USE()) {
		initializeEmbeddedConfluencePromise = (
			await import(
				/* webpackChunkName: "loadable-mobile-app-utils-initialize" */ '@confluence/mobile-app-utils/entry-points/initialize.ts'
			)
		).initialize();
	} else if (isEmbeddedConfluence_DO_NOT_USE()) {
		initializeEmbeddedConfluencePromise = (
			await import(
				/* webpackChunkName: "loadable-embedded-initialize" */ '@confluence/embedded-confluence/entry-points/initialize.ts'
			)
		).initialize();
	}

	let browserNotSupported;
	// Must call this before any browser-helper functions, to ensure user agent has been set first
	setUserAgent();
	// Bypass browser compatibility checks in the case of the Confluence native mobile app;
	// it is the responsibility of the mobile app to ensure the rendering of Confluence
	const shouldCheckBrowserCompatibility = !isNativeMobile();
	if (shouldCheckBrowserCompatibility) {
		browserNotSupported = coreBrowserNotSupported();

		if (browserNotSupported) {
			// oxlint-disable-next-line react-js/no-deprecated
			return void ReactDOM.render(<BrowserNotSupportedPage />, rootNode);
		}
	}

	const sessionDataPromise = (async () => {
		const sessionData = await WaterfallMeasures.run(
			WATERFALL_INITIAL_MEASURES.BOOTSTRAP_SESSION_DATA,
			() => getSessionDataAndInitializeStatsig(),
		);
		const { userId, isLicensed, monolithRegion } = sessionData;

		// Expose synthetic environment flag to the window so downstream consumers
		// can detect it without importing @confluence/statsig-client avoiding possible circular dependencies.
		if (StatsigConfigurations.isKillSwitchOn('cc_e2e_stable_ui')) {
			window.__IS_SYNTHETIC_ENV__ = true;
		}

		// Note: this MUST be done before preload queries below
		// in order to ensure performant AGG queries
		initializeAggRegionSteeringHint(monolithRegion);

		// If page is SSRed preload data that is only needed for hydration
		// If page is not SSRed, preload all the data.
		initializePreloaderFnContext(sessionData);
		void preloadQueriesForSPAHydration();

		try {
			await WaterfallMeasures.run(
				WATERFALL_INITIAL_MEASURES.BOOTSTRAP_INITIALIZE_STORAGE_MANAGER,
				async () => {
					// Initialize browser storage controls after Statsig is ready (for proper feature gate evaluation)
					// This must happen before initializeStorageManager
					await initializeBootstrapStorage({
						userId,
						cloudId: sessionData.cloudId,
					});
					await initializeStorageManager({ userId, isLicensed });
					applyPersistedStatsigOverrides();
					initializeBrowserMetrics(sessionData);
					initializeUFO(sessionData);
					if (!expVal('platform_editor_insm_removal', 'isEnabled', false)) {
						initializeINSM(sessionData);
					}
				},
			);
			return sessionData;
		} catch {
			// HOT-89887: server is giving 403 errors on an expired token, causing Apollo
			// to throw. Uncaught, the React app wouldn't start, and can't acknowledge
			// messages from the service worker; thus the service worker can't trigger a
			// redirect to the login page; thus user gets stuck at the Confluence logo
			// screen. By catching here, we can at least bootstrap the app and proceed to
			// the redirect.
			//
			// https://jira.atlassian.com/browse/CONFCLOUD-71974: IP allowlisting is
			// causing a 403 scenario similar to HOT-89887 above.
			return getEmptySessionData();
		}
	})();

	// Preload loadable id defined in the global __LOADABLE__ array
	// If the page is rendered by React SSR service this promise will be blocking.
	// On normal SPA it is not blocking.
	const preloadLoadablePromise = WaterfallMeasures.run(
		WATERFALL_INITIAL_MEASURES.BOOTSTRAP_LOADABLE,
		() => preloadLoadable(),
	);

	// Preload components/JS chunks as early as possible, when entry point is loaded and
	// route is known, so they'll be ready by the moment when they're actually used.
	void preloadComponentsNoStatsigSupport(window.location.toString(), false);

	Promise.all([
		sessionDataPromise,
		// No-Op if load translation is already called earlier
		tryLoadTranslation(),
		preloadLoadablePromise,
		initializeEmbeddedConfluencePromise,
	]).then(([sessionData]) => {
		// If cloudId is not present in sessionData, we can assume sessionData did not load successfully. Instead of
		// allowing the app to render the red error message, we can render our simplified Error App that fires key analytics
		// events and shows a more pleasing error UI.
		if (sessionData.cloudId === '') {
			let isMobileTakeoverPage = false;
			let isExternalSharePage = false;
			try {
				if (window?.location?.pathname) {
					isMobileTakeoverPage = Boolean(MOBILE_TAKEOVER_PAGE.match(window.location.pathname));
					// Users with qm:-scoped guest session tokens (returning external share visitors)
					// receive tenant: null from SiteInformationQuery because their tokens cannot
					// authenticate against the tenant GraphQL resolver. This causes cloudId to be
					// empty even though the session is valid. ExternalSharePage fetches content via
					// the share token and does not require cloudId, so ErrorAppLoader must not render.
					isExternalSharePage =
						Boolean(EXTERNAL_SHARE.match(window.location.pathname)) ||
						Boolean(ACCESS_LINK.match(window.location.pathname));
				}
			} catch {
				// If anything goes wrong, default to false (don't skip ErrorAppLoader)
				isMobileTakeoverPage = false;
				isExternalSharePage = false;
			}
			if (!isMobileTakeoverPage && !isExternalSharePage) {
				const root = createRoot(rootNode);
				root.render(
					<ErrorAppLoader
						entryPoint="confluence-frontend"
						sessionDataError={getSessionDataError()}
					/>,
				);
				return;
			}
		}

		if (!crypto.randomUUID) {
			crypto.randomUUID = uuidv4;
		}

		// Redux store
		const store = configureStore();
		subscribe(store);

		// When __APOLLO_STATE__ is available in the global variable it means we are bootstrapping SPA over a SSR rendered page.
		// __APOLLO_STATE__ is the serialized apollo cache that is enough to render everything that has already preloaded in preloadLoadable.
		// We are disabling network so on the first render so the components will read from cache only not firing queries again.
		// See https://github.com/apollographql/apollo-client/issues/4814
		if (window['__APOLLO_STATE__'] || window.__SSR_REACT_STREAMING__) {
			client.disableNetworkFetches = true;
		}
		if (window['__APOLLO_AGG_STATE__'] || window.__SSR_REACT_STREAMING__) {
			aggClient.disableNetworkFetches = true;
		}

		if (typeof window !== 'undefined') {
			window.__CC_REACT_DOM_CONTEXT_PATCH__ = true;
		}

		// Workaround for a race condition between Google Translate and React's DOM reconciliation.
		// Google Translate mutates the DOM by wrapping text nodes in <font> elements, reparenting
		// them under a new parent. When React later tries to removeChild or insertBefore on the
		// original parent, the node is no longer a child of that parent, causing a crash.
		// This patches Node.prototype.removeChild and insertBefore to gracefully handle the
		// mismatch instead of throwing.
		//
		// These patches must be applied unconditionally at startup. An on-demand approach that
		// used a MutationObserver to lazily install the patches only when Google Translate was
		// detected was attempted, but there is a race condition: Google Translate can reparent
		// nodes and trigger React's removeChild/insertBefore before the MutationObserver
		// callback fires to install the patches, so React still crashes.
		//
		// See: https://github.com/facebook/react/issues/11538#issuecomment-417504600
		// See: https://product-fabric.atlassian.net/browse/CCP-2409
		handleDOMGoogleTranslateIssues();

		if (process.env.NODE_ENV === 'development') {
			devTrimConsoleTraces();
		}

		// code for regression drill logic. Systematic way of indentifying and tracking performance regressions in web pages
		// will be used to test out criterion, a new perfomacne tracking software
		// for more info visit https://hello.atlassian.net/wiki/spaces/CVMAI/pages/3761808800/Regression+Drill+Plan
		const delayValueForRegression = StatsigConfigurations.getNumber(
			'confluence_frontend_preprod_slow',
			0,
		);

		if (
			delayValueForRegression > 0 &&
			(process.env.CLOUD_ENV === 'dev' ||
				process.env.CLOUD_ENV === 'staging' ||
				process.env.CLOUD_ENV === 'hello' ||
				process.env.CLOUD_ENV === 'branch')
		) {
			const end = performance.now() + Number(delayValueForRegression);
			while (performance.now() < end) {
				//do nothing
			}
		}

		/**
		 * Emotion has 2 ways of SSR support
		 * 1. Render style tags along with the component
		 * 2. Extract the styles with createEmotionServer
		 * Normally we don't need t client side extraction because all the style tags should be in <head>
		 * However with streaming there is no good way to support it.
		 * The "css" key here is special it runs the logic of moving all the style tags to <head> before React hydrates on the the client side.
		 * Reference: https://github.com/emotion-js/emotion/blob/38db311adf024fe8a24b57c687824c47abbd0957/packages/cache/src/index.ts#L63
		 */
		const cache = createCache({ key: 'css' });
		const App = (
			<UFOSegment name="confluence-root">
				<SSRAppTheme />

				<CacheProvider value={cache}>
					<SSRReactRootTiming>
						<Root store={store} apolloClient={client} sessionData={sessionData} />
					</SSRReactRootTiming>
				</CacheProvider>

				<SSRAppScripts />
			</UFOSegment>
		);

		const renderCallback = () => {
			// This is important broadcast in Apollo allows hooks that subscribe to the cache to be updated.
			// Broadcast is disabled until here.
			getApolloCache().startBroadcast();
			getApolloAGGCache().startBroadcast();
			if (window['__APOLLO_STATE__'] || window.__SSR_REACT_STREAMING__) {
				client.disableNetworkFetches = false;
			}
			if (window['__APOLLO_AGG_STATE__'] || window.__SSR_REACT_STREAMING__) {
				aggClient.disableNetworkFetches = false;
			}
			const userAgentParser = new UAParser(navigator.userAgent);
			const { name: browserName, version: browserVersion } = userAgentParser.getBrowser();

			initialization(sessionData, { browserName });
			// Inside of cleanUpLoadablePlaceholders function, it delayed cleaning up loadable placeholders.
			cleanUpLoadablePlaceholders();
			releaseUnusedSSRedLegacyMacrosWithDelay();

			void getAnalyticsWebClient().then((analyticsClient) => {
				// GASv3 analytics client is initialized inside of initialization() so we
				// must call the analytics client after that function has been called.
				if (shouldCheckBrowserCompatibility) {
					analyticsClient.sendOperationalEvent({
						source: 'confluenceRoot',
						action: 'executed',
						actionSubject: 'browserRuntimeCheck',
						attributes: {
							failedCoreBrowserCheck: browserNotSupported,
							browserName,
							browserVersion,
						},
					});
				}
			});
			if (rootNode) {
				initializeVCObserver(rootNode);
			}
		};

		const reportHydrationPhases = ({ shell, syncBoundaries }) => {
			try {
				performance.measure('confluence-hydration:shell', { start: shell.start, end: shell.end });
				if (syncBoundaries) {
					performance.measure('confluence-hydration:sync-boundaries', {
						start: syncBoundaries.start,
						end: syncBoundaries.end,
					});
				}
			} catch {
				// performance.measure with options is unsupported in older browsers
			}
			const hydrationPhases = {
				// From hydrateRoot to the shell render starting
				queueMs: Math.round(shell.start - window['__HYDRATION_START__']),
				shellRenderMs: Math.round(shell.renderMs),
				shellCommitMs: Math.round(shell.commitMs),
				shellActualRenderMs: Math.round(shell.actualRenderMs),
				syncBoundariesRenderMs: Math.round(syncBoundaries?.renderMs ?? 0),
				syncBoundariesCommitMs: Math.round(syncBoundaries?.commitMs ?? 0),
				syncBoundariesActualRenderMs: Math.round(syncBoundaries?.actualRenderMs ?? 0),
				syncBoundariesCommits: syncBoundaries?.commits ?? 0,
				// From the shell render starting to the last sync boundary commit, including the
				// useEffect callbacks that run between them
				totalMs: Math.round((syncBoundaries ?? shell).end - shell.start),
			};
			addUFOCustomData({ hydrationPhases });
			logger.debug`Hydration phases: ${JSON.stringify(hydrationPhases)}`;
		};

		// Set before hydrateRoot when hydration phase timing is enabled
		let onHydrationPhaseRender = null;

		const ConcurrentApp = () => {
			useEffect(() => {
				if (window['__HYDRATION_START__']) {
					logger.debug`Hydration finished in ${Math.floor(
						performance.now() - window['__HYDRATION_START__'],
					)}ms`;
				}
				renderCallback();
			}, []);
			if (onHydrationPhaseRender) {
				// Renders no DOM, so the first client render still matches the server output
				return (
					<Profiler id="confluence-hydration-phases" onRender={onHydrationPhaseRender}>
						{App}
					</Profiler>
				);
			}
			return App;
		};

		// If there are loadable timeouts, there is a very high chance hydration will also fail
		// Trying to hydrate and failing is more expensive than running createRoot from the start so we skip hydration in this case
		if (window['__LOADABLE_TIMEOUTS__']?.length) {
			window['__HYDRATABLE__'] = false;
		}

		const isHydratable = window['__HYDRATABLE__'];

		// The browser Statsig client is initialized by sessionDataPromise before this callback runs.
		fireSSRExperimentExposures();

		if (isHydratable) {
			if (expValEquals('confluence_frontend_hydration_phase_timing', 'isEnabled', true)) {
				onHydrationPhaseRender = createHydrationPhaseTimer(reportHydrationPhases);
			}
			window['__HYDRATION_START__'] = performance.now();
			window['__HYDRATION_FAILED__'] = false;
			logger.debug`Starting hydration`;

			const root = hydrateRoot(rootNode, <ConcurrentApp />, {
				onRecoverableError: (error, errorInfo) => {
					if (
						errorInfo?.digest === SSR_INTENTIONAL_ABORT_DIGEST ||
						errorInfo?.digest === SSR_QUERY_BLOCKED_DIGEST ||
						isNonStreamingError419(error)
					) {
						logger.debug`SSR boundary rendered on the client: ${errorInfo?.digest ?? error.message}`;
						return;
					}
					if (window['__HYDRATION_ERRORS__'] && error?.message) {
						window['__HYDRATION_ERRORS__'].push({
							type: 'ERROR_MESSAGES_FROM_REACT',
							msg: error.message,
							stack: parseHydrationErrorInfo(errorInfo),
						});
					}
					const { hydrationFailed, friendlyMessage, docLink } = matchHydrationError(error);
					if (hydrationFailed) {
						window['__HYDRATION_FAILED__'] = true;
						if (
							StatsigConfigurations.getBoolean('confluence_frontend_hydration_error_event', false)
						) {
							dispatchHydrationError(error, errorInfo);
						}
					}
					if (
						process.env.CLOUD_ENV === 'dev' ||
						process.env.CLOUD_ENV === 'branch' ||
						process.env.CLOUD_ENV === 'staging' ||
						process.env.CLOUD_ENV === 'hello'
					) {
						// eslint-disable-next-line no-console
						console.error(
							`Hydration error: ${friendlyMessage} Please use this link for debugging ${docLink}. Error from react: ${error}`,
						);
					}
				},
			});

			window.__REACT_ROOT__ = root;

			if (window.__SSR_REACT_STREAMING__) {
				// When streaming is enabled, we can schedule a synchronous hydration for all
				// resolved suspense boundaries in the root node as otherwise there is a
				// performance regression in React 18 that impacts TTAI
				//
				// https://hello.atlassian.net/wiki/spaces/~moates/pages/5497372057/Challenges+with+Selective+Hydration
				//
				// When we move to React 19 we can remove this.
				//
				// @see scheduleSyncHydration

				scheduleSyncHydration(rootNode, root);
			}
		} else {
			const concurrentRoot = createRoot(rootNode);
			concurrentRoot.render(<ConcurrentApp />);
		}
	});
});
